import React, { useEffect, useRef, useState } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import Container from '../../../../components/Container'
import api from '../../../../services/api'
import { useToast } from '../../../../hooks/toast'
import { useLoading } from '../../../../hooks/loading'
import { Alert } from '../../../../components/Alert'
import {
  AGENCY_PARTNERSHIP_TYPE_ENUM,
  AGENCY_PARTNERSHIP_TYPE_MAP,
  nameActions
} from '../domain/info'
import { apiDelete, apiList } from '../domain/api'
import { apiDelete as apiDeleteAgencyUser } from '../../AgenciesUsers/domain/api'
import {
  AGENCY_USER_ROLE_MAP,
  nameSource as nameSourceAgenciesUsers
} from '../../AgenciesUsers/domain/info'
import { headers as headersAgenciesUsers } from '../../AgenciesUsers/domain/headers'

import { breadcrumbView } from '../domain/breadcrumb'
import {
  toolsViewCreate,
  toolsViewDelete,
  toolsViewUpdate,
  toolsViewList
} from '../domain/tools'
import Tabs from '../../../../components/Tabs'
import Tab from '../../../../components/Tabs/Tab'
import DataTableForCustomPaginationRoute from '../../../../components/DataTableForCustomPaginationRoute'
import Modal from '../../../../components/Modal'
import { FormAgencyUser } from '../../AgenciesUsers/components/Form'
import { useUpdateDataTable } from '../../../../hooks/dataTable'

type AgencyData = {
  id?: string
  promoter_id: number
  name: string
  cnpj: string
  street: string
  number?: string
  complement?: string
  district: string
  city: string
  uf: string
  zip_code: string
  country: string
  phone_number?: string | null
  whatsapp_number?: string | null
  latitude?: string | null
  longitude?: string | null
  location_link_maps?: string | null
  partnership_type: string
  promoter: {
    id: number
    name: string
  }
  created_at: string
  updated_at: string
}

const AgencyView = (): JSX.Element => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const location = useLocation()
  const { updateDataTable } = useUpdateDataTable()
  const [agency, setAgency] = useState<AgencyData | null>(null)
  const { addToast } = useToast()
  const [alertRemove, setAlertRemove] = useState<{
    open: boolean
  }>({
    open: false
  })

  const searchParameters = useRef([{ agency_id: id }])

  const { disableLoading, activeLoading } = useLoading()

  useEffect(() => {
    async function loadAgency(): Promise<void> {
      activeLoading()
      try {
        const response = await api.get<AgencyData>(apiList(id))
        const { data } = response
        setAgency(data)
        disableLoading()
      } catch (err) {
        const [, baseUrl, path] = location.pathname.split('/')
        disableLoading()
        addToast({
          type: 'error',
          title: 'Error ao carregar o agência',
          description:
            'Houve um error ao carregar o agência, tente novamente mais tarde!'
        })
        if (path.includes('view')) {
          history.push(`/${baseUrl}`)
        } else {
          history.push(`/${baseUrl}/${path}`)
        }
      }
    }
    loadAgency()
  }, [activeLoading, addToast, disableLoading, history, id, location.pathname])

  const handlerClickButtonCancellAlert = () => {
    setAlertRemove({
      open: false
    })
    addToast({
      type: 'info',
      title: 'Operação cancelada.'
    })
  }

  const handlerClickButtonConfirmAlert = async (id: string) => {
    try {
      await api.delete(apiDelete(id))
      setAlertRemove({
        open: false
      })
      addToast({
        type: 'success',
        title: 'Agência removida com sucesso.'
      })
      history.goBack()
    } catch (err) {
      setAlertRemove({
        open: false
      })
      addToast({
        type: 'error',
        title: 'Agência não removida, pois ainda está sendo usada.'
      })
    }
  }

  const [alertRemoveAgencyUser, setAlertRemoveAgencyUser] = useState<{
    open: boolean
    id?: number
    name?: string
  }>({
    open: false
  })

  const handleOnClickRemoveAgencyUser = (id: number, name: string) => {
    setAlertRemoveAgencyUser({
      id,
      open: true,
      name
    })
  }

  const handleOnClickRemove = () => {
    setAlertRemove({
      open: true
    })
  }

  const handlerOnClickButtonConfirmRemoveAgencyUser = async (id: number) => {
    try {
      await api.delete(apiDeleteAgencyUser(String(id)))
      setAlertRemoveAgencyUser({
        open: false
      })
      addToast({
        type: 'success',
        title: 'Usuário removido com sucesso.'
      })
      updateDataTable()
    } catch (err: any) {
      setAlertRemoveAgencyUser({
        open: false
      })
      if (err.response.status === 403) {
        addToast({
          type: 'error',
          title: err.response.data.message
        })
        return
      }
      addToast({
        type: 'error',
        title: 'Usuário não removido, pois ainda está sendo usada.'
      })
    }
  }

  const handlerOnClickButtonCancelRemoveAgencyUser = () => {
    setAlertRemoveAgencyUser({
      open: false
    })
  }

  const [modalCreateAgencyUser, setModalCreateAgencyUser] = useState(false)
  const [modalEditAgencyUser, setModalEditAgencyUser] = useState(false)

  const [currentItemEdit, setCurrentItemEdit] = useState<any>({} as any)

  const handleClickOnClose = () => {
    setModalCreateAgencyUser(false)
    setModalEditAgencyUser(false)
  }

  const handleClickOnOpenModalCreate = () => {
    setModalCreateAgencyUser(true)
  }

  const handleClickOnOpenModalEdit = (item: any) => {
    setCurrentItemEdit({
      agency_id: id,
      name: item.name,
      email: item.email,
      role: item.role,
      id: item.id
    })
    setModalEditAgencyUser(true)
  }

  return (
    <>
      <Container
        pageTitle={nameActions.read.name}
        breadcrumb={breadcrumbView}
        tools={[
          toolsViewUpdate(String(id)),
          toolsViewDelete(() => {
            handleOnClickRemove()
          }),
          toolsViewCreate(),
          toolsViewList()
        ]}
      >
        <div className="form-body">
          <div className="row mb-5">
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Cód.
                </label>
                <p className="fw-bolder fs-6 text-gray-800">{agency?.id}</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Promotor
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {agency?.promoter?.name}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Nome
                </label>
                <p className="fw-bolder fs-6 text-gray-800">{agency?.name}</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  CNPJ
                </label>
                <p className="fw-bolder fs-6 text-gray-800">{agency?.cnpj}</p>
              </div>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Endereço
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {`${agency?.street} - ${agency?.number || 'S/N'}, ${
                    agency?.district
                  },  ${agency?.city}-${agency?.uf}. CEP: ${agency?.zip_code}`}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Whatsapp
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {agency?.whatsapp_number}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Telefone
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {agency?.phone_number}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Tipo de parceria
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {
                    AGENCY_PARTNERSHIP_TYPE_MAP[
                      agency?.partnership_type as AGENCY_PARTNERSHIP_TYPE_ENUM
                    ]
                  }
                </p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Cadastrado em
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {agency?.created_at}
                </p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="col-form-label fw-bold fs-6form-label fw-bold fs-6">
                  Atualizado em
                </label>
                <p className="fw-bolder fs-6 text-gray-800">
                  {agency?.updated_at}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="row mt-8">
          <Tabs>
            <Tab title="Usuários">
              <div className="card-header p-0 d-flex - align-items-center">
                <div className="card-title m-0 fw-bolder fs-6 text-gray-800"></div>
                <div
                  className=""
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'center'
                  }}
                >
                  <a
                    href="#"
                    onClick={handleClickOnOpenModalCreate}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="fa fa-plus" /> Adicionar
                  </a>
                </div>
              </div>
              {
                <DataTableForCustomPaginationRoute
                  paginationUrl="partnerships/agenciesUsers/pagination"
                  source={nameSourceAgenciesUsers}
                  headers={headersAgenciesUsers}
                  searchParameters={searchParameters.current}
                  notHasChildren
                  customHeaders={[
                    {
                      name: 'Grupo de usuário',
                      field: 'role',
                      sortable: false,
                      element: item => (
                        <span>
                          {
                            AGENCY_USER_ROLE_MAP[
                              item.role as keyof typeof AGENCY_USER_ROLE_MAP
                            ]
                          }
                        </span>
                      )
                    }
                  ]}
                  actions={[
                    {
                      name: 'Atualizar',
                      title: 'Atualizar',
                      element: item => {
                        return (
                          <span
                            className={'fa fa-edit'}
                            onClick={() => handleClickOnOpenModalEdit(item)}
                          ></span>
                        )
                      }
                    },
                    {
                      name: 'Remover',
                      title: 'Remover',
                      element: item => {
                        return (
                          <span
                            className={'fa fa-remove'}
                            onClick={() =>
                              handleOnClickRemoveAgencyUser(item.id, item.name)
                            }
                          ></span>
                        )
                      }
                    }
                  ]}
                />
              }
            </Tab>
            <></>
          </Tabs>
        </div>
      </Container>
      <Alert
        message={`Tem certeza que deseja excluir o registro ${agency?.name} ?`}
        onClickCancellButton={handlerClickButtonCancellAlert}
        onClickConfirmButton={() => {
          handlerClickButtonConfirmAlert(String(agency?.id))
        }}
        isActive={alertRemove.open}
      />
      <Alert
        message={`Tem certeza que deseja excluir o registro ${alertRemoveAgencyUser.name} ?`}
        onClickCancellButton={handlerOnClickButtonCancelRemoveAgencyUser}
        onClickConfirmButton={() =>
          handlerOnClickButtonConfirmRemoveAgencyUser(
            Number(alertRemoveAgencyUser.id)
          )
        }
        isActive={alertRemoveAgencyUser.open}
      />

      {modalCreateAgencyUser && (
        <Modal
          onClickButtonCancel={handleClickOnClose}
          isOpenModal={modalCreateAgencyUser}
          pageTitle={'Adicionar Usuário'}
          style={{
            stylesModal: {
              minWidth: '60vw'
            }
          }}
          Children={
            <FormAgencyUser
              typeForm="create"
              isOpenInModal={{
                handleOnClose: handleClickOnClose,
                agency_id: Number(id)
              }}
            />
          }
        />
      )}
      {modalEditAgencyUser && (
        <Modal
          onClickButtonCancel={handleClickOnClose}
          isOpenModal={modalEditAgencyUser}
          pageTitle={'Atualizar Usuário'}
          style={{
            stylesModal: {
              minWidth: '60vw'
            }
          }}
          Children={
            <FormAgencyUser
              typeForm="update"
              initialValues={{
                ...currentItemEdit,
                idUpdate: currentItemEdit?.id
              }}
              isOpenInModal={{
                handleOnClose: handleClickOnClose,
                agency_id: Number(id)
              }}
            />
          }
        />
      )}
    </>
  )
}

export default AgencyView
